import type { RegisteredRouter } from '@tanstack/react-router';
import { Link, Outlet } from '@tanstack/react-router';
import type { RoutePaths } from '@tanstack/router-core';

type ValidRoutePath = Exclude<RoutePaths<RegisteredRouter['routeTree']>, `/${string}/`>;

const tabBaseStyles = 'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium';

const colorStyles = {
  primary: {
    selected: `${tabBaseStyles} border-primary-500 text-primary-600`,
    unselected: `${tabBaseStyles} border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700`,
  },
  orange: {
    selected: `${tabBaseStyles} border-orange-500 text-orange-600`,
    unselected: `${tabBaseStyles} border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700`,
  },
};

type TabColor = keyof typeof colorStyles;

interface RouteTabsProps {
  tabs: readonly { to: ValidRoutePath; label: string }[];
  color?: TabColor;
  ariaLabel?: string;
}

export function RouteTabs({ tabs, color = 'primary', ariaLabel = 'Tabs' }: RouteTabsProps) {
  const styles = colorStyles[color];

  return (
    <>
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 px-4" aria-label={ariaLabel}>
          {tabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              params={(prev) => prev}
              activeProps={{ className: styles.selected }}
              inactiveProps={{ className: styles.unselected }}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>
      <Outlet />
    </>
  );
}
